import { useEffect, useState } from "react";
import useAuth from "@/hooks/useAuth";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import { buildBody, buildUrl } from "./helpers";
import type { FetchOptions, FetchResult } from "./types";

/**
 * The `clients-web` twin of this hook also sends `x-store-id`. Admins are cross-tenant — there is no
 * active store to scope a request to — so the header is absent here and every path is store-free.
 */
export default function useFetch<T = unknown>(path: string, baseOptions: FetchOptions = {}, init: boolean = true): FetchResult<T> {
    if (!path) throw new Error("Path is required for useFetch");

    const token = useAuth((state) => state.token);
    const dropDeadSession = useAuth((state) => state.dropDeadSession);
    const lang = useLang((state) => state.lang);
    const t = useTranslation();

    const [pendingOptions, setPendingOptions] = useState<FetchOptions | null>(null);
    const [work, setWork] = useState(init ? 1 : 0);
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(init);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<number | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        const options = { ...baseOptions, ...pendingOptions };

        async function fetching() {
            setLoading(true);
            setError(null);

            const url = buildUrl(path, options);
            const headers: Record<string, string> = { "x-language": lang ?? "ar" };
            if (token) headers["Authorization"] = `Bearer ${token}`;

            const requestInit: RequestInit = { method: options.method ?? "GET", headers, signal: options.signal ?? controller.signal };

            if (options.data) {
                const { body, contentType } = buildBody(options.data);
                requestInit.body = body;
                if (contentType) headers["Content-Type"] = contentType;
            }

            try {
                const response = await fetch(url, requestInit);
                setStatus(response.status);

                /**
                 * Not everything that answers this URL is the API. nginx rejects an oversized upload
                 * with its own HTML 413 page and a dead upstream with an HTML 502, and parsing those
                 * before checking `ok` threw into the catch below — so a request the server had
                 * already explained was reported to the user as a network failure it was not.
                 */
                const json = await response.json().catch(() => null);

                /**
                 * 401 means this token is no longer a session — revoked, expired, or belonging to an
                 * admin who has been deleted or deactivated. Keeping it leaves someone on a dashboard
                 * where every request fails silently, so it is dropped and the page returns to sign-in.
                 *
                 * Guarded on having sent a token: login and OTP are public, and a 401 there is a wrong
                 * code rather than a dead session.
                 */
                if (response.status === 401 && token) dropDeadSession();
                else if (response.ok && json) setData(json);
                else if (json?.message) setError(json.message);
                else setError(response.status === 413 ? t.errors.tooLarge : t.errors.unknown);
            } catch (err) {
                // An abort means this request was superseded or the component unmounted, so its
                // result belongs to nothing on screen. Returning before `setLoading(false)` matters:
                // clearing it here would end the loading state while the replacement request is
                // still in flight, and the screen would paint its empty state over live data.
                if ((err as Error)?.name === "AbortError") return;
                setError(t.errors.network);
            }

            setLoading(false);
        }

        if (work > 0) fetching();
        return () => controller.abort();
    }, [work]);

    const invoke = (overrideOptions?: FetchOptions) => {
        if (overrideOptions) setPendingOptions(overrideOptions);
        setWork((prev) => prev + 1);
    };

    return { data, loading, error, status, invoke };
}
